import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { TrendingUp, Calendar, Award, Flame, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBereketStats, getBereketReflectionToday, saveBereketReflection } from '../lib/api';
import { toast } from 'sonner';

export default function BereketStats({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [stats, setStats] = useState(null);
  const [showReflection, setShowReflection] = useState(false);
  const [reflection, setReflection] = useState({
    question1: '',
    question2: '',
    question3: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchTodayReflection();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await getBereketStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju statistike' : 'Error loading stats');
    }
  };

  const fetchTodayReflection = async () => {
    try {
      const data = await getBereketReflectionToday();
      if (data) {
        setReflection({
          question1: data.question1 || '',
          question2: data.question2 || '',
          question3: data.question3 || '',
        });
      }
    } catch (error) {
      console.error('Error fetching reflection:', error);
    }
  };

  const saveReflection = async () => {
    setLoading(true);
    try {
      await saveBereketReflection(reflection);
      setShowReflection(false);
      toast.success(lang === 'hr' ? 'Refleksija spremljena' : 'Reflection saved');
    } catch (error) {
      console.error('Error saving reflection:', error);
      toast.error(lang === 'hr' ? 'Greška pri spremanju' : 'Error saving reflection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/bereket"
              className="p-2 rounded-full hover:bg-stone-100 transition-colors"
              data-testid="back-to-bereket"
            >
              <ArrowLeft className="w-5 h-5 text-[#57534E]" />
            </Link>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
                {lang === 'hr' ? 'Statistika Bereketa' : 'Barakah Statistics'}
              </h1>
              <p className="text-[#57534E] font-body">
                {lang === 'hr'
                  ? 'Tvoj duhovni napredak'
                  : 'Your spiritual progress'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowReflection(true)}
            data-testid="open-reflection-button"
            className="px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-md"
          >
            {lang === 'hr' ? 'Dnevna Refleksija' : 'Daily Reflection'}
          </button>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-primary to-primary/80 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <p className="text-sm opacity-90">
                    {lang === 'hr' ? 'Danas' : 'Today'}
                  </p>
                </div>
                <p
                  className="text-4xl font-heading font-bold mb-1"
                  data-testid="stats-today-points"
                >
                  {stats.today_points}
                </p>
                <p className="text-sm opacity-90">
                  {lang === 'hr' ? 'bodova' : 'points'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent to-accent/80 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <p className="text-sm opacity-90">
                    {lang === 'hr' ? 'Ovaj tjedan' : 'This Week'}
                  </p>
                </div>
                <p
                  className="text-4xl font-heading font-bold mb-1"
                  data-testid="stats-week-points"
                >
                  {stats.week_points}
                </p>
                <p className="text-sm opacity-90">
                  {lang === 'hr' ? 'bodova' : 'points'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6" />
                  </div>
                  <p className="text-sm opacity-90">
                    {lang === 'hr' ? 'Ovaj mjesec' : 'This Month'}
                  </p>
                </div>
                <p
                  className="text-4xl font-heading font-bold mb-1"
                  data-testid="stats-month-points"
                >
                  {stats.month_points}
                </p>
                <p className="text-sm opacity-90">
                  {lang === 'hr' ? 'bodova' : 'points'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-400 to-orange-500 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Flame className="w-6 h-6" />
                  </div>
                  <p className="text-sm opacity-90">
                    {lang === 'hr' ? 'Niz' : 'Streak'}
                  </p>
                </div>
                <p
                  className="text-4xl font-heading font-bold mb-1"
                  data-testid="stats-streak"
                >
                  {stats.current_streak}
                </p>
                <p className="text-sm opacity-90">
                  {lang === 'hr' ? 'dana' : 'days'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">
                  {lang === 'hr' ? 'Današnja Razina' : "Today's Level"}
                </h3>
                <div className="flex items-center gap-4">
                  <div className="text-6xl">{stats.today_level_emoji}</div>
                  <div>
                    <p className="font-heading font-semibold text-2xl text-[#292524]">
                      {lang === 'hr'
                        ? stats.today_level
                            .replace(
                              'Day of Spiritual Breakthrough',
                              'Dan duhovnog proboja'
                            )
                            .replace('Day of Barakah', 'Dan bereketa')
                            .replace('Day of Growth', 'Dan rasta')
                            .replace('Day of Awareness', 'Dan svjesnosti')
                            .replace('Starting the Journey', 'Početak puta')
                        : stats.today_level}
                    </p>
                    <p className="text-sm text-[#57534E] mt-1">
                      {lang === 'hr'
                        ? `${stats.today_points} bodova danas`
                        : `${stats.today_points} points today`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">
                  {lang === 'hr'
                    ? 'Aktivnosti po kategoriji'
                    : 'Activities by Category'}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>🟢</span>
                      <span className="text-[#57534E]">
                        {lang === 'hr' ? 'Osnovni' : 'Basic'}
                      </span>
                    </div>
                    <span
                      className="font-semibold text-[#292524]"
                      data-testid="basic-activities-count"
                    >
                      {stats.activities_by_category.basic || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>🔵</span>
                      <span className="text-[#57534E]">
                        {lang === 'hr' ? 'Srednji' : 'Medium'}
                      </span>
                    </div>
                    <span
                      className="font-semibold text-[#292524]"
                      data-testid="medium-activities-count"
                    >
                      {stats.activities_by_category.medium || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>🟣</span>
                      <span className="text-[#57534E]">
                        {lang === 'hr' ? 'Napredni' : 'Advanced'}
                      </span>
                    </div>
                    <span
                      className="font-semibold text-[#292524]"
                      data-testid="advanced-activities-count"
                    >
                      {stats.activities_by_category.advanced || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
              <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">
                {lang === 'hr' ? 'Razine Postignuća' : 'Achievement Levels'}
              </h3>
              <div className="space-y-3">
                <div
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    stats.today_points >= 5
                      ? 'bg-green-50 border-2 border-green-200'
                      : 'bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🌱</span>
                    <div>
                      <p className="font-medium text-[#292524]">
                        {lang === 'hr' ? 'Dan svjesnosti' : 'Day of Awareness'}
                      </p>
                      <p className="text-sm text-[#57534E]">5-10 bodova</p>
                    </div>
                  </div>
                  {stats.today_points >= 5 && (
                    <Award className="w-6 h-6 text-green-600" />
                  )}
                </div>

                <div
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    stats.today_points >= 11
                      ? 'bg-green-50 border-2 border-green-200'
                      : 'bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🌿</span>
                    <div>
                      <p className="font-medium text-[#292524]">
                        {lang === 'hr' ? 'Dan rasta' : 'Day of Growth'}
                      </p>
                      <p className="text-sm text-[#57534E]">11-20 bodova</p>
                    </div>
                  </div>
                  {stats.today_points >= 11 && (
                    <Award className="w-6 h-6 text-green-600" />
                  )}
                </div>

                <div
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    stats.today_points >= 21
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🌳</span>
                    <div>
                      <p className="font-medium text-[#292524]">
                        {lang === 'hr' ? 'Dan bereketa' : 'Day of Barakah'}
                      </p>
                      <p className="text-sm text-[#57534E]">21-35 bodova</p>
                    </div>
                  </div>
                  {stats.today_points >= 21 && (
                    <Award className="w-6 h-6 text-primary" />
                  )}
                </div>

                <div
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    stats.today_points >= 36
                      ? 'bg-accent/10 border-2 border-accent'
                      : 'bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">✨</span>
                    <div>
                      <p className="font-medium text-[#292524]">
                        {lang === 'hr'
                          ? 'Dan duhovnog proboja'
                          : 'Day of Spiritual Breakthrough'}
                      </p>
                      <p className="text-sm text-[#57534E]">36+ bodova</p>
                    </div>
                  </div>
                  {stats.today_points >= 36 && (
                    <Award className="w-6 h-6 text-accent" />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showReflection && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-heading font-semibold text-[#292524] mb-6">
              {lang === 'hr' ? 'Dnevna Refleksija' : 'Daily Reflection'}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr'
                    ? 'Što sam učinio svjesno radi Allaha?'
                    : 'What did I do consciously for Allah?'}
                </label>
                <textarea
                  value={reflection.question1}
                  onChange={(e) =>
                    setReflection({ ...reflection, question1: e.target.value })
                  }
                  data-testid="reflection-question1"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr'
                    ? 'Gdje sam mogao bolje?'
                    : 'Where could I have done better?'}
                </label>
                <textarea
                  value={reflection.question2}
                  onChange={(e) =>
                    setReflection({ ...reflection, question2: e.target.value })
                  }
                  data-testid="reflection-question2"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr'
                    ? 'Koji trenutak danas mi je donio najviše unutarnjeg mira?'
                    : 'Which moment today brought me the most inner peace?'}
                </label>
                <textarea
                  value={reflection.question3}
                  onChange={(e) =>
                    setReflection({ ...reflection, question3: e.target.value })
                  }
                  data-testid="reflection-question3"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={saveReflection}
                  disabled={loading}
                  data-testid="save-reflection-button"
                  className="flex-1 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? lang === 'hr'
                      ? 'Spremanje...'
                      : 'Saving...'
                    : lang === 'hr'
                    ? 'Spremi'
                    : 'Save'}
                </button>
                <button
                  onClick={() => setShowReflection(false)}
                  className="px-6 py-3 bg-stone-100 text-[#292524] rounded-full font-medium hover:bg-stone-200 transition-all"
                >
                  {lang === 'hr' ? 'Odustani' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}